// linkCrypto.js — decrypt signing-link path segments (Phase 2c).
// Matches server.requiresign.com/app/utils/encrypt.js (AES-256-CBC).
// The key is already embedded in every historically-sent link; using it
// client-side only to read the JWT `region` claim for API routing.

import CryptoJS from "crypto-js";
import {
  DEFAULT_REGION,
  isValidRegion,
  decodeJwtPayload,
  setActiveRegion,
} from "../../configs/regionConfig";
import { refreshRegionExports } from "../../apis/apis";

const SECRET_KEY = "vOVH6sdmpNWjRRIqCc7rdxs01lwHzfr3"; // 32 chars — same as server encrypt.js

/**
 * Decrypt a path segment produced by server encryptData().
 * Format: "<ivHex>:<cipherHex>"
 */
export function decryptLinkSegment(encrypted) {
  try {
    if (!encrypted || typeof encrypted !== "string" || !encrypted.includes(":")) {
      return null;
    }
    const [ivHex, dataHex] = encrypted.split(":");
    if (!ivHex || !dataHex) return null;
    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
    const iv = CryptoJS.enc.Hex.parse(ivHex);
    const encryptedWA = CryptoJS.enc.Hex.parse(dataHex);
    const cipherParams = CryptoJS.lib.CipherParams.create({
      ciphertext: encryptedWA,
    });
    const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const text = decrypted.toString(CryptoJS.enc.Utf8);
    return text || null;
  } catch (_e) {
    return null;
  }
}

/**
 * From an encrypted JWT path segment, return the residency region code.
 * Missing claim / decrypt failure → "us".
 */
export function regionFromEncryptedJwtSegment(encryptedToken) {
  const jwt = decryptLinkSegment(encryptedToken);
  if (!jwt) return DEFAULT_REGION;
  // Raw JWT may have been the plaintext; also accept already-decrypted JWT.
  const payload = decodeJwtPayload(jwt) || decodeJwtPayload(encryptedToken);
  const code = payload?.region;
  return isValidRegion(code) ? String(code).toLowerCase() : DEFAULT_REGION;
}

/**
 * Pin the editor session's API/link bases from a signing-link token segment.
 */
export function applyRegionFromSigningToken(encryptedToken) {
  const code = regionFromEncryptedJwtSegment(encryptedToken);
  setActiveRegion(code);
  refreshRegionExports();
  return code;
}
